import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExecution, useExecutionResults, useWorkflow } from '@/hooks/useDocFlow';
import { EXECUTION_STATUS_LABELS, EXECUTION_STATUS_COLORS } from '@/types/docflow';
import ExecutionResultsTable from './ExecutionResultsTable';

const ExecutionResultsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('docflow');
  const { data: execution, isLoading: execLoading } = useExecution(id || '');
  const { data: results } = useExecutionResults(id || '');
  const { data: workflow } = useWorkflow(execution?.workflow_id || '');

  // Find output fields from workflow's output_designer node
  const designerNode = workflow?.nodes?.find((n) => n.type === 'output_designer');
  const outputFields = designerNode?.config?.fields;

  // Find sheet output info
  const sheetNode = workflow?.nodes?.find((n) => n.type === 'sheet_output');
  const spreadsheetId = sheetNode?.config?.spreadsheet_id;

  if (execLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Execution not found</div>
      </div>
    );
  }

  const progressPercent = execution.total_files > 0
    ? Math.round(((execution.done_files + execution.failed_files) / execution.total_files) * 100)
    : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(execution.workflow_id ? `/app/docflow/${execution.workflow_id}/run` : '/app/docflow')}
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {t('execution.results')}
            {workflow && <span className="text-gray-400 font-normal"> — {workflow.name}</span>}
          </h1>
        </div>
      </div>

      {/* Execution summary */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('execution.status')}</div>
            <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${EXECUTION_STATUS_COLORS[execution.status]}`}>
              {EXECUTION_STATUS_LABELS[execution.status]}
            </span>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('execution.progress')}</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    execution.status === 'completed' ? 'bg-green-500' :
                    execution.status === 'failed' ? 'bg-red-500' :
                    execution.status === 'running' ? 'bg-blue-500' :
                    'bg-gray-400'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {execution.done_files}/{execution.total_files}
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('execution.startedAt')}</div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {new Date(execution.started_at).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('execution.completedAt')}</div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {execution.completed_at ? new Date(execution.completed_at).toLocaleString() : '—'}
            </div>
          </div>
        </div>

        {execution.error && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
            {execution.error}
          </div>
        )}

        {spreadsheetId && (
          <div className="mt-3">
            <a
              href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
            >
              {t('execution.viewSheet')}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </div>

      {/* Results table */}
      {results && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <ExecutionResultsTable results={results} fields={outputFields} />
        </div>
      )}
    </div>
  );
};

export default ExecutionResultsPage;
